import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh?: () => Promise<void> | void;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({ children, onRefresh }) => {
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Configuration
  const PULL_THRESHOLD = 80;
  const MAX_PULL = 120;

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // Ignore touches on the bottom bar
      const target = e.target as HTMLElement;
      if (target.closest('.bottom-bar-ignore-pull')) {
        return;
      }

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop <= 0) {
        setStartY(e.touches[0].clientY);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Ignore touches on the bottom bar
      const target = e.target as HTMLElement;
      if (target.closest('.bottom-bar-ignore-pull')) {
        return;
      }

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const touchY = e.touches[0].clientY;
      const pull = touchY - startY;

      // Only allow pulling if we are at the top and pulling down
      if (scrollTop <= 0 && pull > 0) {
        // Prevent default only if we are significantly pulling to avoid blocking normal scrolling too easily
        if (pull > 5) {
          // e.preventDefault(); // removed to allow scrolling if user changes mind, let's see if it works smoothly
        }
        
        // Add resistance
        const dampenedPull = Math.min(pull * 0.4, MAX_PULL);
        setCurrentY(dampenedPull);
      }
    };

    const handleTouchEnd = async () => {
      if (currentY > PULL_THRESHOLD) {
        setRefreshing(true);
        setCurrentY(PULL_THRESHOLD); // Snap to threshold
        
        // Trigger refresh
        if (onRefresh) {
          await onRefresh();
        } else {
          // Default behavior: reload page
          window.location.reload();
          return; // Don't reset state as page will reload
        }
        
        setRefreshing(false);
      }
      setCurrentY(0);
    };

    const element = contentRef.current;
    if (!element) return;

    // Use non-passive listeners to be able to prevent default if needed (though we handle it carefully)
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [startY, currentY, onRefresh]);

  return (
    <div ref={contentRef} className="min-h-[calc(100vh-7.5rem)] relative">
      {/* Refresh Indicator */}
      <div 
        className="absolute top-0 left-0 right-0 flex justify-center pointer-events-none transition-transform duration-200 ease-out"
        style={{ 
          transform: `translateY(${currentY > 0 ? currentY - 40 : -40}px)`,
          opacity: currentY > 0 ? Math.min(currentY / PULL_THRESHOLD, 1) : 0
        }}
      >
        <div className={`bg-white rounded-full p-2 shadow-md border border-slate-200 ${refreshing ? 'animate-spin' : ''}`}>
          <RefreshCw 
            size={20} 
            className={`${currentY > PULL_THRESHOLD ? 'text-green-600' : 'text-slate-400'}`}
            style={{ transform: `rotate(${currentY * 2}deg)` }}
          />
        </div>
      </div>

      {/* Content */}
      <div 
        style={{ 
          transform: `translateY(${currentY}px)`,
          transition: 'transform 0.2s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};
